// API client for communicating with Supabase backend
import { projectId, publicAnonKey } from '../../utils/supabase/info';
import { Project, DataRow, TemplateSet, GenerationLog, GenerationPreset } from '@/types';
import type { ParsedTemplateCSV } from './templateCsv';

// Re-export types for convenience
export type {
  Project,
  DataRow,
  TemplateSet,
  GenerationLog,
  TemplateSetContent,
  HeadlineSlot,
  DescriptionSlot,
  TemplateVariant,
  KeywordItem,
  CalloutItem,
  SitelinkItem,
} from '@/types';

const API_BASE = `https://${projectId}.supabase.co/functions/v1/make-server-def7bef3`;

// Shared-secret enforced by the Edge Function. Set in Cloudflare Pages env;
// must match the LUNARGRID_API_KEY env on Supabase.
const API_KEY = (import.meta.env.VITE_LUNARGRID_API_KEY as string | undefined) ?? '';

async function fetchAPI<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${publicAnonKey}`,
      'X-API-Key': API_KEY,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error (${response.status}): ${errorText}`);
  }

  // 204 No Content or empty body — treat as undefined T (callers asking for void will expect this)
  if (response.status === 204) return undefined as T;
  // Only parse JSON when the server actually says JSON. Health checks and similar endpoints
  // can return text/plain 200s which would otherwise crash with a parse error.
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    const text = await response.text();
    return (text as unknown) as T;
  }
  return response.json();
}

// Tables
export async function getAvailableTables(): Promise<{ table_name: string }[]> {
  return fetchAPI<{ table_name: string }[]>('/tables');
}

// Projects
export async function getAllProjects(): Promise<Project[]> {
  return fetchAPI<Project[]>('/projects');
}

export async function getProject(id: string): Promise<Project> {
  return fetchAPI<Project>(`/projects/${id}`);
}

export async function createProject(project: Omit<Project, 'id' | 'created_at' | 'updated_at'>): Promise<Project> {
  return fetchAPI<Project>('/projects', {
    method: 'POST',
    body: JSON.stringify(project),
  });
}

export async function updateProject(id: string, updates: Partial<Project>): Promise<Project> {
  return fetchAPI<Project>(`/projects/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteProject(id: string): Promise<void> {
  await fetchAPI<void>(`/projects/${id}`, {
    method: 'DELETE',
  });
}

// Data rows (read-only)
export async function getCitiesByProject(projectId: string): Promise<DataRow[]> {
  return fetchAPI<DataRow[]>(`/projects/${projectId}/cities`);
}

// Template Sets
export async function getTemplateSetsByProject(projectId: string): Promise<TemplateSet[]> {
  return fetchAPI<TemplateSet[]>(`/projects/${projectId}/template-sets`);
}

export async function getTemplateSet(id: string): Promise<TemplateSet> {
  return fetchAPI<TemplateSet>(`/template-sets/${id}`);
}

export async function createTemplateSet(templateSet: Omit<TemplateSet, 'id' | 'created_at' | 'updated_at'>): Promise<TemplateSet> {
  return fetchAPI<TemplateSet>('/template-sets', {
    method: 'POST',
    body: JSON.stringify(templateSet),
  });
}

export async function updateTemplateSet(id: string, updates: Partial<TemplateSet>): Promise<TemplateSet> {
  return fetchAPI<TemplateSet>(`/template-sets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteTemplateSet(id: string): Promise<void> {
  await fetchAPI<void>(`/template-sets/${id}`, {
    method: 'DELETE',
  });
}

export async function duplicateTemplateSet(id: string, newName: string): Promise<TemplateSet> {
  return fetchAPI<TemplateSet>(`/template-sets/${id}/duplicate`, {
    method: 'POST',
    body: JSON.stringify({ newName }),
  });
}

// CSV import — creates a new TemplateSet or updates an existing one from a parsed CSV.
// Throws if the parser surfaced any errors.

export interface ImportTemplateCSVOptions {
  projectId: string;
  parsed: ParsedTemplateCSV;
  // Create mode: omit targetTemplateSetId. The new template uses parsed.meta.name (or nameOverride).
  // Update mode: provide targetTemplateSetId. Existing name/language stay unless parsed.meta provides them.
  targetTemplateSetId?: string;
  nameOverride?: string;
  languageOverride?: string;
}

export async function importTemplateFromCSV(opts: ImportTemplateCSVOptions): Promise<TemplateSet> {
  const { projectId, parsed, targetTemplateSetId, nameOverride, languageOverride } = opts;
  if (parsed.errors.length > 0) {
    throw new Error(`Template CSV has ${parsed.errors.length} error(s); resolve before importing.`);
  }
  if (!parsed.content) {
    throw new Error('Template CSV has no usable content. The parse failed before any content was assembled.');
  }
  const content = parsed.content;

  if (targetTemplateSetId) {
    const existing = await getTemplateSet(targetTemplateSetId);
    const updates: Partial<TemplateSet> = {
      template_content: content,
    };
    const nextName = nameOverride ?? parsed.meta.name;
    if (nextName && nextName !== existing.name) updates.name = nextName;
    const nextLang = languageOverride ?? parsed.meta.language_code;
    if (nextLang && nextLang !== existing.language_code) updates.language_code = nextLang;
    return updateTemplateSet(targetTemplateSetId, updates);
  }

  const name = nameOverride ?? parsed.meta.name;
  if (!name || !name.trim()) {
    throw new Error('Template name is required. Set Meta:name in the CSV or provide a name override.');
  }
  return createTemplateSet({
    project_id: projectId,
    name: name.trim(),
    language_code: (languageOverride ?? parsed.meta.language_code ?? 'fr').trim(),
    status: 'draft',
    generation_settings: { filters: [] },
    template_content: content,
  });
}

// Generation Logs
export async function getGenerationLogsByProject(projectId: string): Promise<GenerationLog[]> {
  return fetchAPI<GenerationLog[]>(`/projects/${projectId}/generation-logs`);
}

export async function getGenerationLog(id: string): Promise<GenerationLog> {
  return fetchAPI<GenerationLog>(`/generation-logs/${id}`);
}

export async function createGenerationLog(log: Omit<GenerationLog, 'id' | 'generated_at'>): Promise<GenerationLog> {
  return fetchAPI<GenerationLog>('/generation-logs', {
    method: 'POST',
    body: JSON.stringify(log),
  });
}

// ── Generation Presets ──

export async function getGenerationPresetsByProject(projectId: string): Promise<GenerationPreset[]> {
  return fetchAPI<GenerationPreset[]>(`/projects/${projectId}/generation-presets`);
}

export async function createGenerationPreset(preset: Omit<GenerationPreset, 'id' | 'created_at' | 'updated_at'>): Promise<GenerationPreset> {
  return fetchAPI<GenerationPreset>('/generation-presets', {
    method: 'POST',
    body: JSON.stringify(preset),
  });
}

export async function updateGenerationPreset(id: string, updates: Partial<GenerationPreset>): Promise<GenerationPreset> {
  return fetchAPI<GenerationPreset>(`/generation-presets/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function deleteGenerationPreset(id: string): Promise<void> {
  await fetchAPI(`/generation-presets/${id}`, { method: 'DELETE' });
}

// Current Project (using localStorage for now as it's UI-specific state)
export function getCurrentProject(): string | null {
  return localStorage.getItem('adgenpro_current_project');
}

export function setCurrentProject(projectId: string): void {
  localStorage.setItem('adgenpro_current_project', projectId);
}